// lib/telefonie/anrufListe.ts
// Anruf-Zeile → Zeilenmodell für die Anrufliste des Tages in der Telefon-Seite. Pure
// Logik, getrennt von der Anruf-Karte (die zeigt den *laufenden* Anruf, hier geht es
// um die *Historie* darunter) — beide teilen sich aber dieselbe Rollen-Zuordnung.
import { ROLLE, type AnrufZeile, type AnrufZiel } from './anrufKarte';

export interface ListenEintrag {
  id: string;
  /** Name des Anrufers, sonst die Nummer. */
  titel: string;
  /** Lesbare Rolle, wenn erkannt. */
  rolle: string | null;
  /** m:ss, oder null ohne Dauer (z. B. verpasst). */
  dauerText: string | null;
  verpasst: boolean;
  /** Immer die normalisierte Nummer — nur die ist tatsächlich wählbar. */
  rueckrufNummer: string | null;
  richtung: 'eingehend' | 'ausgehend';
  /** Startzeitpunkt, roh für `zeitKurz`. */
  begonnenAm: string;
  /** Sprungziel zum Kontakt, wenn erkannt — additiv aus treffer_typ/treffer_id,
   *  dieselbe Form wie AnrufKarte.ziel. Mit anrufZielPfad() aus anrufZiel.ts in
   *  eine Route übersetzen. Null bei unbekannter Nummer. */
  ziel: AnrufZiel | null;
}

/** Sekunden → m:ss. Dieselbe Formatierung wie in `anrufKarte.ts`, hier lokal
 *  gehalten: die Anrufliste zeigt reine Dauer ohne Statustext davor, ein
 *  gemeinsamer Export würde nur eine unnötige Kopplung zweier Anzeigen erzeugen. */
function dauerText(sekunden: number): string {
  const m = Math.floor(sekunden / 60);
  const s = sekunden % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Anruf-Zeile in einen Eintrag der Anrufliste übersetzen. */
export function zuListenEintrag(a: AnrufZeile): ListenEintrag {
  const nummer = a.nummer_e164 ?? a.nummer_roh ?? null;
  const erkannt = !!(a.treffer_typ && a.treffer_id && (a.treffer_name || '').trim() !== '');

  return {
    id: a.id,
    titel: erkannt ? (a.treffer_name as string) : (nummer ?? 'Unbekannte Nummer'),
    rolle: erkannt ? (ROLLE[a.treffer_typ as string] ?? null) : null,
    dauerText: a.dauer_sekunden ? dauerText(a.dauer_sekunden) : null,
    verpasst: a.status === 'verpasst',
    rueckrufNummer: a.nummer_e164 ?? null,
    richtung: a.richtung,
    begonnenAm: a.begonnen_am,
    ziel: erkannt ? { typ: a.treffer_typ as string, id: a.treffer_id as string } : null,
  };
}

const TAG_MS = 24 * 60 * 60 * 1000;

/** Tagesanfang (lokale Zeitzone) als Zeitstempel, für den kalendarischen Tagesabstand. */
function tagStart(zeitpunkt: number): number {
  const d = new Date(zeitpunkt);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Zeitpunkt für die Anrufliste kurz darstellen: heute → Uhrzeit, gestern → „Gestern",
 * älter → Datum. Arbeitet mit lokaler Zeit (Browser-Zeitzone des Nutzers), damit die
 * Anzeige zur Wanduhr passt, an der der Nutzer gerade sitzt.
 */
export function zeitKurz(iso: string, jetzt: number): string {
  const datum = new Date(iso);
  const tageDiff = Math.round((tagStart(jetzt) - tagStart(datum.getTime())) / TAG_MS);

  if (tageDiff === 0) {
    const hh = String(datum.getHours()).padStart(2, '0');
    const mm = String(datum.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }
  if (tageDiff === 1) return 'Gestern';

  const tt = String(datum.getDate()).padStart(2, '0');
  const mo = String(datum.getMonth() + 1).padStart(2, '0');
  return `${tt}.${mo}.`;
}
